import { FONT_CANDIDATES, OS_ORIGINS, type FontCandidate, type FontOrigin } from '../data/fonts';
import { fontListBits } from '../entropy/estimates';
import type { Collector } from './types';
import { prop, signal, unavailable } from './util';

export const BASE_FAMILIES = ['monospace', 'sans-serif', 'serif'] as const;
export type BaseFamily = (typeof BASE_FAMILIES)[number];
export interface Dim {
  w: number;
  h: number;
}
export type Measurements = Record<BaseFamily, Dim>;

const TEST_STRING = 'mmmmmmmmmmlli1WQ@#&%ÆŒ';
const TEST_SIZE = '72px';

/**
 * A candidate is installed if rendering `"<candidate>", <base>` measures differently from `<base>`
 * alone for any base family: the browser used the candidate instead of falling back.
 */
export function isDetected(baseline: Measurements, candidate: Measurements): boolean {
  return BASE_FAMILIES.some((b) => candidate[b].w !== baseline[b].w || candidate[b].h !== baseline[b].h);
}

export function detectFonts(baseline: Measurements, measured: Record<string, Measurements>): string[] {
  return Object.entries(measured)
    .filter(([, m]) => isDetected(baseline, m))
    .map(([name]) => name)
    .sort((a, b) => a.localeCompare(b));
}

export interface OsGuess {
  os: string | null;
  scores: Record<string, number>;
}

/** Guess the OS from which OS-specific fonts appeared. Needs at least 3 markers to commit. */
export function guessOs(detected: string[], candidates: FontCandidate[] = FONT_CANDIDATES): OsGuess {
  const origins = new Map(candidates.map((c) => [c.name, c.origin]));
  const scores: Record<string, number> = {};
  for (const name of detected) {
    const os = OS_ORIGINS[origins.get(name) as FontOrigin];
    if (os) scores[os] = (scores[os] ?? 0) + 1;
  }
  const best = Object.entries(scores).sort((a, b) => b[1] - a[1])[0];
  return { os: best && best[1] >= 3 ? best[0] : null, scores };
}

/** Detected fonts that are not defaults of the guessed OS (or common core fonts). */
export function extraFonts(detected: string[], os: string | null, candidates: FontCandidate[] = FONT_CANDIDATES): string[] {
  const origins = new Map(candidates.map((c) => [c.name, c.origin]));
  return detected.filter((name) => {
    const origin = origins.get(name);
    if (!origin || origin === 'common') return false;
    return OS_ORIGINS[origin] !== os;
  });
}

function measureAll(names: string[]): { baseline: Measurements; measured: Record<string, Measurements> } {
  const container = document.createElement('div');
  container.setAttribute('aria-hidden', 'true');
  const s = container.style;
  s.setProperty('position', 'absolute');
  s.setProperty('left', '-9999px');
  s.setProperty('top', '0');
  s.setProperty('visibility', 'hidden');
  s.setProperty('white-space', 'nowrap');

  const make = (family: string) => {
    const span = document.createElement('span');
    span.textContent = TEST_STRING;
    span.style.setProperty('font-family', family);
    span.style.setProperty('font-size', TEST_SIZE);
    span.style.setProperty('font-style', 'normal');
    span.style.setProperty('font-weight', 'normal');
    span.style.setProperty('letter-spacing', 'normal');
    span.style.setProperty('line-height', 'normal');
    span.style.setProperty('position', 'absolute');
    container.appendChild(span);
    return span;
  };

  const baseSpans = Object.fromEntries(BASE_FAMILIES.map((b) => [b, make(b)])) as Record<BaseFamily, HTMLSpanElement>;
  const quote = (n: string) => `"${n.replace(/["\\]/g, '')}"`;
  const candidateSpans = names.map((name) => ({
    name,
    spans: Object.fromEntries(BASE_FAMILIES.map((b) => [b, make(`${quote(name)}, ${b}`)])) as Record<BaseFamily, HTMLSpanElement>,
  }));

  document.body.appendChild(container);
  try {
    // All spans are in the DOM before any read, so layout happens once.
    const read = (spans: Record<BaseFamily, HTMLSpanElement>): Measurements =>
      Object.fromEntries(BASE_FAMILIES.map((b) => [b, { w: spans[b].offsetWidth, h: spans[b].offsetHeight }])) as Measurements;
    const baseline = read(baseSpans);
    const measured: Record<string, Measurements> = {};
    for (const c of candidateSpans) measured[c.name] = read(c.spans);
    return { baseline, measured };
  } finally {
    container.remove();
  }
}

export const fontsCollector: Collector = {
  id: 'fonts',
  title: 'Fonts',
  description: `Which of ${FONT_CANDIDATES.length} common fonts are installed, detected by measuring text width. No permission prompt is involved.`,
  async collect() {
    if (typeof document === 'undefined' || !document.body) {
      return [
        unavailable('fonts.list', 'Installed fonts', 'No document to measure in'),
        unavailable('fonts.osGuess', 'OS guess from fonts', 'No document to measure in'),
        unavailable('fonts.apis', 'Font APIs', 'No document'),
      ];
    }

    const apis = {
      documentFonts: 'fonts' in document,
      queryLocalFonts: typeof prop(window, 'queryLocalFonts') === 'function',
    };
    const apiSignal = signal('fonts.apis', 'Font APIs', apis, {
      display: `document.fonts: ${apis.documentFonts ? 'yes' : 'no'} · queryLocalFonts(): ${apis.queryLocalFonts ? 'exists (not called; it would prompt)' : 'not available'}`,
    });

    let detected: string[];
    try {
      const { baseline, measured } = measureAll(FONT_CANDIDATES.map((c) => c.name));
      if (BASE_FAMILIES.every((b) => baseline[b].w === 0)) throw new Error('Text measurement returned zero widths');
      detected = detectFonts(baseline, measured);
    } catch (err) {
      const note = err instanceof Error ? err.message : 'Measurement failed';
      return [unavailable('fonts.list', 'Installed fonts', note), unavailable('fonts.osGuess', 'OS guess from fonts', note), apiSignal];
    }

    const guess = guessOs(detected);
    const extras = extraFonts(detected, guess.os);
    const restricted = extras.length === 0;

    return [
      signal('fonts.list', 'Installed fonts', detected, {
        display: `${detected.length} of ${FONT_CANDIDATES.length} detected (${extras.length} beyond OS defaults)\n${detected.join(', ') || '(none)'}`,
        bits: fontListBits(extras.length),
        note: restricted
          ? 'Only OS default fonts are visible, which many browsers share.'
          : `Fonts beyond your OS defaults: ${extras.slice(0, 12).join(', ')}${extras.length > 12 ? `, +${extras.length - 12} more` : ''}`,
      }),
      signal('fonts.osGuess', 'OS guess from fonts', { os: guess.os, scores: guess.scores, extras: extras.length }, {
        display: guess.os
          ? `${guess.os} (${Object.entries(guess.scores)
              .map(([os, n]) => `${os}: ${n}`)
              .join(', ')} OS-specific fonts)`
          : 'Not enough OS-specific fonts to tell',
      }),
      apiSignal,
    ];
  },
};
